import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import React from "react";
import styled from "styled-components";
import { ExclamationRedLargeIcon } from "../../../CommonUI/Icon/ExclamationRedLargeIcon";
import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { FlowEditContext } from "../../FlowEditContext";
import { IFlowDialog } from "../IFlowDialog";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";

const NotificationDialog = styled(Dialog)<{
    action: {
        background: string;
        color: string;
    };
    width?: number;
}>`
    .MuiDialogTitle-root {
        padding: 28px 24px 0;

        .MuiTypography-root {
            text-align: center;
            padding: 0;
        }
    }

    .MuiDialogActions-root {
        justify-content: center;
        padding: 0;

        .MuiButton-root {
            padding: 10px 33px;

            .MuiButton-label {
                font-style: normal;
                font-weight: bold;
                font-size: 14px;
                line-height: 140%;
                /* identical to box height, or 20px */
            }
        }

        .MuiButton-containedPrimary {
            background: ${(props) => props.action.background};
            border-radius: 4px;
            color: ${(props) => props.action.color};
        }
    }

    .MuiDialog-paper {
        padding: 32px;

        max-width: ${(props) => props.width || 600}px;
    }

    .MuiDialogContent-root {
        padding: 0 !important;

        .MuiDialogContentText-root {
            font-style: normal;
            font-weight: 600;
            font-size: 16px;
            line-height: 140%;
            text-align: center;

            color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
            padding: 0;
        }
    }
`;

interface INotification {
    editContext: FlowEditContext;
}

export const Notification: React.FC<INotification> = (props: INotification) => {
    function onClose() {
        if (props.editContext.notification!.onCancel) {
            props.editContext.notification!.onCancel();
        }
        props.editContext.notification = undefined;
    }

    function onSuccess() {
        if (props.editContext.notification!.onSuccess) {
            props.editContext.notification!.onSuccess();
        }
        props.editContext.notification = undefined;
    }

    return (
        <ObserveState
            listenTo={props.editContext}
            properties={[FlowEditContext.NOTIFICATION]}
            control={() => {
                let notification: IFlowDialog | undefined = props.editContext.notification;
                if (!notification) return <></>;

                let type = notification.type;
                if (!type) {
                    type = "error";
                }

                let action = {
                    background: UIStyleConstants.RED_COLOR,
                    color: UIStyleConstants.WHITE_COLOR,
                };

                let width: undefined | number = 600;

                if (type === "confirm") {
                    action = {
                        background: UIStyleConstants.BLUE_COLOR,
                        color: UIStyleConstants.WHITE_COLOR,
                    };
                    width = 400;
                }

                return (
                    <NotificationDialog
                        open={true}
                        onClose={onClose}
                        action={action}
                        width={width}
                    >
                        {type === "error" && (
                            <DialogTitle>
                                <ExclamationRedLargeIcon />
                            </DialogTitle>
                        )}
                        <DialogContent>
                            <DialogContentText data-testid={`error-dialog-content`}>
                                {notification!.message}
                            </DialogContentText>
                        </DialogContent>
                        {(notification!.onCancel || notification!.onSuccess) && (
                            <DialogActions>
                                {notification!.onCancel && (
                                    <Button
                                        variant="contained"
                                        onClick={onClose}
                                        data-testid="btn-dialog-cancel"
                                    >
                                        Cancel
                                    </Button>
                                )}
                                {notification!.onSuccess && (
                                    <Button
                                        variant="contained"
                                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                        onClick={onSuccess}
                                        data-testid="btn-dialog-success"
                                    >
                                        Ok
                                    </Button>
                                )}
                            </DialogActions>
                        )}
                        {notification!.actions &&
                            notification!.actions.map((a, idx) => (
                                <DialogActions
                                    style={a.wrapperStyle}
                                    key={idx}
                                >
                                    {a.items.map((item, index) => {
                                        return <React.Fragment key={index}>{item.renderUI()}</React.Fragment>;
                                    })}
                                </DialogActions>
                            ))}
                    </NotificationDialog>
                );
            }}
        />
    );
};
